/**
 * DV-TEMP-001: Temporal Demo Component
 * Events v2 timeline viewer with filters and URL persistence
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Database, RefreshCw, Copy, Check } from 'lucide-react';
import { EventFilters } from './EventFilters';
import { VerticalTimeline } from './VerticalTimeline';
import { TestBlock } from './TestBlock';
import { ExecutionSelector } from './ExecutionSelector';
import { TemporalLinks } from './TemporalLinks';
import { extractWorkflowIdentifiers, generateAllTemporalLinks } from '@/lib/temporal-links';
import { useExecutionEvents } from '@/hooks/useExecutionEvents';
import { useTelemetry } from './useTelemetry';
import { useStatusReport } from './useStatusReport';
import { PermissionNotice, NotFoundNotice, ServerErrorNotice } from './ErrorNotices';
import { getDemoError, logDemoError } from '@/lib/demo-error-handler';
import type { TimelineFilters } from '@/types/execution-events';
import { Stage, type StageName } from '@/lib/shared-types';
// import type { EventType } from '@/types/events-v2'; // Removed unused import

// Flexible time range for UI state
interface FlexibleTimeRange {
  from?: string;
  to?: string;
}

// Flexible filters for UI (allows partial time range)
interface FlexibleTimelineFilters extends Omit<TimelineFilters, 'timeRange'> {
  timeRange?: FlexibleTimeRange;
}
import type { LogEntry } from './LogsPanel';
import { cn } from '@/utils/cn';

export interface TemporalDemoProps {
  executionId?: string;
  onExecutionChange?: (executionId: string) => void;
  onLog?: (entry: LogEntry) => void;
  className?: string;
  'data-testid'?: string;
}

// Default execution ID for testing (valid UUID format required by backend)
// Updated to use execution ID that has events in the database (5cad6696-21a0-42f6-91d3-53f382399294 has 15 events)
const DEFAULT_EXECUTION_ID = '5cad6696-21a0-42f6-91d3-53f382399294';

// Valid stage values for validation
const VALID_STAGES = Stage.options;

// Type validation functions
const isValidStage = (stage: string): stage is StageName => {
  return VALID_STAGES.includes(stage as StageName);
};

// UUID validation function
const isValidUUID = (uuid: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

// EventType validation is handled by the useExecutionEvents hook

// Deep equality check for filters to prevent URL sync loops
const filtersEqual = (a: FlexibleTimelineFilters, b: FlexibleTimelineFilters): boolean => {
  // Compare stage arrays
  const aStages = a.stage || [];
  const bStages = b.stage || [];
  if (aStages.length !== bStages.length || !aStages.every((s, i) => s === bStages[i])) {
    return false;
  }

  // Compare eventType arrays
  const aEventTypes = a.eventType || [];
  const bEventTypes = b.eventType || [];
  if (aEventTypes.length !== bEventTypes.length || !aEventTypes.every((t, i) => t === bEventTypes[i])) {
    return false;
  }

  // Compare persona arrays
  const aPersonas = a.persona || [];
  const bPersonas = b.persona || [];
  if (aPersonas.length !== bPersonas.length || !aPersonas.every((p, i) => p === bPersonas[i])) {
    return false;
  }

  // Compare time ranges
  const aTimeRange = a.timeRange || {};
  const bTimeRange = b.timeRange || {};
  if (aTimeRange.from !== bTimeRange.from || aTimeRange.to !== bTimeRange.to) {
    return false;
  }

  return true;
};

// Parse URL search params to filters
const parseFiltersFromUrl = (searchParams: URLSearchParams): FlexibleTimelineFilters => {
  const filters: FlexibleTimelineFilters = {};

  // Parse stage array with validation
  const stages = searchParams.getAll('stage').filter(isValidStage);
  if (stages.length > 0) {
    filters.stage = stages;
  }

  // Parse event type array (validation handled by hook)
  const types = searchParams.getAll('type');
  if (types.length > 0) {
    filters.eventType = types as any; // Type validation handled by useExecutionEvents hook
  }

  // Parse persona array - support both CSV and multiple params for backwards compatibility
  const personaParams = searchParams.getAll('persona');
  const personaCsv = searchParams.get('personas'); // Also support 'personas' as CSV

  let personas: string[] = [];
  if (personaCsv) {
    // CSV format: ?personas=director,narrator,host
    personas = personaCsv.split(',').map(p => p.trim()).filter(p => p.length > 0);
  } else if (personaParams.length > 0) {
    // Multiple param format: ?persona=director&persona=narrator (backwards compatibility)
    personas = personaParams;
  }

  if (personas.length > 0) {
    filters.persona = personas;
  }

  // Parse time range
  const from = searchParams.get('from');
  const to = searchParams.get('to');
  if (from || to) {
    filters.timeRange = {};
    if (from) filters.timeRange.from = from;
    if (to) filters.timeRange.to = to;
  }

  return filters;
};

// Convert filters to URL search params
const filtersToUrlParams = (
  filters: FlexibleTimelineFilters,
  // DV-TEMP-004: Additional state for URL persistence
  additionalState?: {
    liveMode?: boolean;
    pollingInterval?: number;
    virtualization?: boolean;
  }
): URLSearchParams => {
  const params = new URLSearchParams();

  // Add stages
  if (filters.stage?.length) {
    filters.stage.forEach(stage => params.append('stage', stage));
  }

  // Add event types
  if (filters.eventType?.length) {
    filters.eventType.forEach(type => params.append('type', type));
  }

  // Add personas as CSV for clean URLs
  if (filters.persona?.length) {
    params.set('personas', filters.persona.join(','));
  }

  // Add time range
  if (filters.timeRange?.from) {
    params.set('from', filters.timeRange.from);
  }
  if (filters.timeRange?.to) {
    params.set('to', filters.timeRange.to);
  }

  // DV-TEMP-004: Enhanced URL persistence for full state
  if (additionalState?.liveMode !== undefined) {
    params.set('live', additionalState.liveMode.toString());
  }
  if (additionalState?.pollingInterval !== undefined && additionalState.pollingInterval !== 30000) {
    params.set('polling', additionalState.pollingInterval.toString());
  }
  if (additionalState?.virtualization !== undefined && !additionalState.virtualization) {
    params.set('virtual', 'false'); // Only persist if disabled (default is enabled)
  }

  return params;
};

export function TemporalDemo({
  executionId = DEFAULT_EXECUTION_ID,
  onExecutionChange,
  onLog,
  className,
  'data-testid': testId = 'temporal-demo'
}: TemporalDemoProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse initial filters from URL
  const [filters, setFilters] = useState<FlexibleTimelineFilters>(() => {
    return parseFiltersFromUrl(searchParams);
  });

  // DV-TEMP-004: Live mode toggle (default OFF)
  const [isLiveMode, setIsLiveMode] = useState(false);

  // DV-TEMP-004: Configurable polling intervals with visible defaults
  const [pollingConfig, setPollingConfig] = useState({
    enabled: true,
    intervalMs: 3000, // 3 seconds default
    liveIntervalMs: 1000 // 1 second for live mode (with jitter added in hook)
  });

  // DV-TEMP-004: Status report copy state
  const [copySuccess, setCopySuccess] = useState(false);

  // Track recent API calls for correlation reporting
  const [recentApiCalls, setRecentApiCalls] = useState<any[]>([]);

  // Enhanced logger that tracks API calls for correlation
  const enhancedLogger = useCallback((logEntry: any) => {
    // Track API calls for status report correlation
    if (logEntry.meta?.requestId) {
      setRecentApiCalls(prev => [logEntry, ...prev.slice(0, 9)]); // Keep last 10 API calls
    }

    // Forward to original logger
    if (onLog) {
      onLog(logEntry);
    }
  }, [onLog]);

  // Initialize telemetry
  const { mark, time } = useTelemetry({
    onLog: enhancedLogger
  });

  // DV-TEMP-004: Status report builder
  const { buildTemporalReport } = useStatusReport();

  // Convert flexible filters to strict filters for the hook
  const strictFilters = useMemo((): TimelineFilters => {
    const result: TimelineFilters = {
      stage: filters.stage,
      eventType: filters.eventType,
      persona: filters.persona
    };

    // Only include timeRange if both from and to are present
    if (filters.timeRange?.from && filters.timeRange?.to) {
      result.timeRange = {
        from: filters.timeRange.from,
        to: filters.timeRange.to
      };
    }

    return result;
  }, [filters]);

  // Sync URL with filters when filters change
  const handleFiltersChange = useCallback((newFilters: FlexibleTimelineFilters) => {
    setFilters(newFilters);

    // Update URL
    const newParams = filtersToUrlParams(newFilters);
    setSearchParams(newParams, { replace: true });

    // Log filter change
    if (onLog) {
      onLog({
        id: `filter-change-${Date.now()}`,
        ts: new Date().toISOString(),
        level: 'info',
        text: 'Event filters changed',
        meta: {
          filters: newFilters,
          activeFilters: Object.keys(newFilters).filter(key =>
            newFilters[key as keyof TimelineFilters]
          ).length,
          executionId
        }
      });
    }
  }, [setSearchParams, onLog]);

  // Sync URL changes back to filters (with deep equality guard to prevent loops)
  useEffect(() => {
    const urlFilters = parseFiltersFromUrl(searchParams);
    // Only update filters if they actually changed
    if (!filtersEqual(filters, urlFilters)) {
      setFilters(urlFilters);
    }
  }, [searchParams, filters]);

  // Check if execution ID is valid
  const isValidExecution = isValidUUID(executionId);

  // Fetch events with current filters and live mode support
  const {
    events,
    eventsByStage,
    isLoading,
    isError,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refresh: refetch,
    totalEvents,
    wsState,
    deduplicationStats
  } = useExecutionEvents({
    executionId,
    ...strictFilters,
    limit: 20,
    enabled: pollingConfig.enabled && isValidExecution,
    enableLiveUpdates: isLiveMode,
    refetchInterval: isLiveMode ? pollingConfig.liveIntervalMs : pollingConfig.intervalMs
  });

  // Log WebSocket status changes to LogsPanel
  useEffect(() => {
    if (onLog && wsState) {
      onLog({
        id: `websocket-status-${Date.now()}`,
        ts: new Date().toISOString(),
        level: 'info',
        text: `WebSocket status: ${wsState.status}, connected: ${wsState.isConnected}`,
        meta: {
          source: 'temporal',
          liveMode: isLiveMode,
          webSocketStatus: wsState.status,
          executionId
        }
      });
    }
  }, [isLiveMode, wsState?.status, wsState?.isConnected, onLog, executionId]);

  // Log WebSocket connection status changes
  useEffect(() => {
    if (onLog && wsState.lastUpdate) {
      onLog({
        id: `websocket-update-${Date.now()}`,
        ts: new Date().toISOString(),
        level: 'info',
        text: `WebSocket update received (${wsState.status})`,
        meta: {
          source: 'temporal',
          webSocketStatus: wsState.status,
          isConnected: wsState.isConnected,
          isPollingFallback: wsState.isPollingFallback,
          executionId
        }
      });
    }
  }, [wsState.lastUpdate, wsState.status, wsState.isConnected, wsState.isPollingFallback, onLog, executionId]);

  // Log events data updates
  useEffect(() => {
    // Safety check: ensure events is defined and is an array
    if (events && Array.isArray(events) && events.length > 0 && onLog) {
      onLog({
        id: `events-loaded-${Date.now()}`,
        ts: new Date().toISOString(),
        level: 'info',
        text: `Loaded ${events.length} timeline events`,
        meta: {
          executionId,
          totalEvents,
          hasNextPage,
          filters
        }
      });
    }
  }, [events?.length, totalEvents, hasNextPage, filters, onLog]);

  // Wrapped fetch next page with timing
  const handleLoadMore = useCallback(async () => {
    if (!fetchNextPage) return;

    await time('fetch_next_page', async () => {
      await fetchNextPage();
    }, {
      executionId,
      currentEventCount: events?.length || 0
    });
  }, [fetchNextPage, time, events?.length]);

  // Wrapped refetch with timing
  const handleRefetch = useCallback(async () => {
    await time('refetch_events', async () => {
      await refetch();
    }, {
      executionId,
      filters
    });
  }, [refetch, time, filters]);

  // DV-TEMP-004: Handle Live mode toggle
  const handleLiveModeToggle = useCallback(() => {
    const newLiveMode = !isLiveMode;
    setIsLiveMode(newLiveMode);

    // Log Live mode toggle
    if (onLog) {
      onLog({
        id: `live-mode-toggle-${Date.now()}`,
        ts: new Date().toISOString(),
        level: 'info',
        text: `Live mode ${newLiveMode ? 'enabled' : 'disabled'}`,
        meta: {
          liveMode: newLiveMode,
          pollingInterval: newLiveMode ? '1000ms + jitter' : '3000ms + jitter',
          executionId
        }
      });
    }
  }, [isLiveMode, onLog, executionId]);

  // DV-TEMP-004: Calculate temporal metadata and timing metrics
  const calculateTemporalMetadata = useCallback(() => {
    // Safety check: ensure events is defined and is an array
    if (!events || !Array.isArray(events)) {
      const identifiers = extractWorkflowIdentifiers(executionId);
      const links = generateAllTemporalLinks(identifiers);
      return {
        workflowId: identifiers.workflowId,
        runId: identifiers.runId,
        workflowLinks: links
      };
    }

    const workflowRunId = events[0]?.workflow_run_id;

    const identifiers = extractWorkflowIdentifiers(executionId, workflowRunId);
    const links = generateAllTemporalLinks(identifiers);

    return {
      workflowId: identifiers.workflowId,
      runId: identifiers.runId,
      workflowLinks: links
    };
  }, [events, executionId]);

  const calculateTimingMetrics = useCallback(() => {
    // Safety check: ensure events is defined and is an array
    if (!events || !Array.isArray(events) || events.length === 0) return null;

    // Calculate server clock skew (difference between first event timestamp and now)
    const firstEvent = events[0];
    const firstEventTime = new Date(firstEvent.timestamp).getTime();
    const now = Date.now();
    const serverClockSkew = now - firstEventTime;

    // Calculate stage timings from events
    const stageTimings: Record<string, { startTime?: string; endTime?: string; durationMs?: number }> = {};

    // Group events by stage
    const eventsByStage = events.reduce((acc, event) => {
      if (!event.stage) return acc;
      if (!acc[event.stage]) acc[event.stage] = [];
      acc[event.stage].push(event);
      return acc;
    }, {} as Record<string, any[]>);

    // Calculate start/end times for each stage
    Object.entries(eventsByStage).forEach(([stage, stageEvents]) => {
      const sortedEvents = stageEvents.sort((a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

      const startEvent = sortedEvents[0];
      const endEvent = sortedEvents[sortedEvents.length - 1];

      // Look for completion events to determine actual end time
      const completionEvent = sortedEvents.find(e =>
        e.event_type === 'stage_completed' ||
        e.event_type === 'content_ready' ||
        e.event_type === 'stage_failed'
      );

      const actualEndEvent = completionEvent || endEvent;
      const startTime = startEvent.timestamp;
      const endTime = actualEndEvent.timestamp;
      const durationMs = new Date(endTime).getTime() - new Date(startTime).getTime();

      stageTimings[stage] = {
        startTime,
        endTime: completionEvent ? endTime : undefined, // Only set endTime if stage actually completed
        durationMs: completionEvent ? durationMs : undefined
      };
    });

    // Find pipeline start time (earliest event timestamp)
    const pipelineStartTime = events
      .map(e => new Date(e.timestamp).getTime())
      .reduce((earliest, time) => Math.min(earliest, time), Infinity);

    return {
      stageTimings,
      pipelineStartTime: new Date(pipelineStartTime).toISOString(),
      serverClockSkew
    };
  }, [events]);

  // DV-UX-002: Detect failed stages and provide retry functionality
  const failedStages = useMemo(() => {
    return events
      .filter(event => event.event_type === 'stage_failed' || event.event_type === 'error')
      .map(event => event.stage)
      .filter(Boolean)
      .filter((stage, index, arr) => arr.indexOf(stage) === index); // Remove duplicates
  }, [events]);

  const hasFailedStages = failedStages.length > 0;

  // DV-UX-002: Retry failed stages functionality
  // @ts-ignore - Function prepared for future use in DV-UX-002
  const retryFailedStages = useCallback(async () => {
    if (!hasFailedStages) return;

    try {
      // In a real implementation, this would call an API to retry the failed stages
      // For now, we'll just log the retry attempt and refresh the data
      if (onLog) {
        onLog({
          id: `retry-failed-${Date.now()}`,
          ts: new Date().toISOString(),
          level: 'info',
          text: `Attempting to retry ${failedStages.length} failed stages: ${failedStages.join(', ')}`,
          meta: {
            source: 'temporal',
            action: 'retry_failed_stages',
            executionId,
            failedStages,
            stageCount: failedStages.length
          }
        });
      }

      // Refresh the timeline to get updated data
      await handleRefetch();

      // Note: In a real implementation, you would call something like:
      // await retryExecutionStages(executionId, failedStages);

    } catch (error) {
      if (onLog) {
        onLog({
          id: `retry-failed-error-${Date.now()}`,
          ts: new Date().toISOString(),
          level: 'error',
          text: `Failed to retry stages: ${error}`,
          meta: {
            source: 'temporal',
            action: 'retry_failed_stages_error',
            executionId,
            error: error instanceof Error ? error.message : 'Unknown error'
          }
        });
      }
    }
  }, [hasFailedStages, failedStages, onLog, executionId, handleRefetch]);

  // DV-TEMP-004: Copy status report with correlation ID logging and enhanced temporal data
  const copyStatusReport = useCallback(async () => {
    try {
      const correlationId = `temporal-status-${Date.now()}`;

      // Calculate temporal metadata and timing metrics
      const temporalMetadata = calculateTemporalMetadata();
      const timingMetrics = calculateTimingMetrics();

      const report = buildTemporalReport(
        executionId,
        filters,
        events,
        [], // Recent logs - empty for now, could be populated from LogsPanel
        recentApiCalls, // Recent API calls with correlation IDs
        isLiveMode,
        isLiveMode ? pollingConfig.liveIntervalMs : pollingConfig.intervalMs, // Actual polling interval
        {
          totalEvents,
          loadedCount: events?.length || 0,
          hasNextPage: hasNextPage || false
        },
        deduplicationStats, // DV-TEMP-004: Include deduplication metrics
        temporalMetadata, // DV-TEMP-004: Include temporal workflow metadata
        timingMetrics || undefined // DV-TEMP-004: Include timing metrics and clock skew
      );

      await navigator.clipboard.writeText(report);
      setCopySuccess(true);

      // Log successful copy with correlation ID
      if (onLog) {
        onLog({
          id: correlationId,
          ts: new Date().toISOString(),
          level: 'info',
          text: 'Temporal status report copied to clipboard',
          meta: {
            source: 'temporal',
            action: 'status_report_copied',
            correlationId,
            reportSize: report.length,
            executionId,
            eventsCount: events?.length || 0,
            filtersActive: Object.keys(filters).length,
            liveMode: isLiveMode
          }
        });
      }

      // Reset success state after 2 seconds
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      // Log copy failure
      if (onLog) {
        onLog({
          id: `temporal-copy-error-${Date.now()}`,
          ts: new Date().toISOString(),
          level: 'error',
          text: `Failed to copy temporal status report: ${error}`,
          meta: {
            source: 'temporal',
            error: error instanceof Error ? error.message : 'Unknown error',
            executionId
          }
        });
      }
    }
  }, [buildTemporalReport, executionId, filters, events, isLiveMode, totalEvents, hasNextPage, onLog, pollingConfig, recentApiCalls, deduplicationStats, calculateTemporalMetadata, calculateTimingMetrics]);

  // Convert error to DemoError format and log it
  const demoError = useMemo(() => {
    if (!isError || !error) return null;

    const demoErr = getDemoError(error);
    if (demoErr && onLog) {
      // Log the structured error
      const logEntry = logDemoError(demoErr);
      onLog(logEntry);
    }

    return demoErr;
  }, [isError, error, onLog]);

  // Component mount telemetry
  useEffect(() => {
    mark('temporal_demo_mount', {
      executionId,
      hasUrlFilters: Object.keys(filters).length > 0
    });

    // Debug logging for execution ID
    if (onLog) {
      onLog({
        id: `temporal-demo-debug-${Date.now()}`,
        ts: new Date().toISOString(),
        level: 'info',
        text: `TemporalDemo mounted with execution ID: ${executionId}`,
        meta: {
          source: 'temporal',
          component: 'TemporalDemo',
          executionId,
          executionIdLength: executionId?.length || 0,
          executionIdValid: isValidUUID(executionId),
          defaultUsed: executionId === DEFAULT_EXECUTION_ID,
          hasFilters: Object.keys(filters).length > 0
        }
      });
    }

    return () => {
      mark('temporal_demo_unmount', {
        executionId,
        eventsLoaded: events?.length || 0
      });
    };
  }, [mark, filters, events?.length]);

  return (
    <div className={cn("space-y-6", className)} data-testid={testId}>
      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h2
            className="text-xl font-light"
            style={{ color: 'var(--accent)' }}
          >
            Events v2 Timeline Viewer
          </h2>
          {/* DV-TEMP-004: Live mode toggle and status report copy */}
          <div className="flex items-center gap-3">
            {/* Copy Status Report Button */}
            <button
              onClick={copyStatusReport}
              className="flex items-center gap-1 px-2 py-1 rounded text-xs border transition-colors focus:outline-none focus:ring-2"
              style={{
                backgroundColor: 'var(--bg-surface)',
                borderColor: 'var(--border-default)',
                color: 'var(--fg-primary)',
                ['--hover-bg' as any]: 'var(--bg-interactive-hover)',
                ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--bg-surface)';
              }}
              onFocus={(e) => {
                e.currentTarget.style.boxShadow = 'var(--focus-ring)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
              data-testid="dv-status-copy"
              title="Copy Status Report"
            >
              {copySuccess ? (
                <>
                  <Check className="w-3 h-3" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3" />
                  Copy Status
                </>
              )}
            </button>

            {/* Retry Failed Stages Button */}
            {hasFailedStages && (
              <button
                onClick={retryFailedStages}
                className="flex items-center gap-1 px-2 py-1 rounded text-xs border transition-colors focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: 'var(--bg-surface)',
                  borderColor: 'var(--accent)',
                  color: 'var(--accent)'
                }}
                title="Retry Failed Stages"
              >
                <RefreshCw className="w-3 h-3" />
                Retry Failed ({failedStages.length})
              </button>
            )}

            {/* Live mode toggle */}
            <label
              className="flex items-center gap-2 text-sm cursor-pointer"
              style={{ color: 'var(--fg-primary)' }}
            >
              <input
                type="checkbox"
                checked={isLiveMode}
                onChange={handleLiveModeToggle}
                className="sr-only"
                data-testid="temporal-live-toggle"
              />
              <div
                className={`relative w-10 h-6 rounded-full transition-colors duration-200 ${
                  isLiveMode
                    ? 'bg-green-500'
                    : 'bg-gray-300'
                }`}
                style={{
                  backgroundColor: isLiveMode ? 'var(--accent)' : 'var(--border-default)'
                }}
              >
                <div
                  className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full transition-transform duration-200 ${
                    isLiveMode ? 'translate-x-4' : 'translate-x-0'
                  }`}
                  style={{ backgroundColor: 'var(--bg-app)' }}
                />
              </div>
              <span>Live</span>
              {isLiveMode && (
                <div className="flex items-center gap-1">
                  {/* Small pulsing dot */}
                  <div
                    className="w-2 h-2 rounded-full animate-pulse"
                    style={{ backgroundColor: 'var(--accent)' }}
                  />
                  {/* Enhanced LIVE pill */}
                  <div
                    className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: 'var(--accent)',
                      color: 'white'
                    }}
                  >
                    <div className="w-1 h-1 bg-white rounded-full animate-ping" />
                    LIVE
                  </div>
                </div>
              )}
            </label>
          </div>
        </div>
        <p
          className="text-sm"
          style={{ color: 'var(--fg-muted)' }}
        >
          Explore pipeline events with real-time filtering and server-side pagination.
          {isLiveMode && (
            <span>
              {' '}Live mode: WebSocket {wsState.isConnected ? 'connected' : 'disconnected'}
              {wsState.isPollingFallback && ', polling fallback active'}.
            </span>
          )}
        </p>

        {/* DV-TEMP-004: Visible polling configuration */}
        <div className="flex items-center gap-4 mt-2 text-xs" style={{ color: 'var(--fg-muted)' }}>
          <div className="flex items-center gap-1">
            <div
              className={`w-2 h-2 rounded-full ${pollingConfig.enabled ? 'bg-green-400' : 'bg-gray-400'}`}
              title={pollingConfig.enabled ? 'Polling enabled' : 'Polling disabled'}
            />
            <span>
              Polling: {pollingConfig.enabled ? 'ON' : 'OFF'}
            </span>
          </div>

          {pollingConfig.enabled && (
            <div className="flex items-center gap-1">
              <span>
                Interval: {isLiveMode
                  ? `${pollingConfig.liveIntervalMs/1000}s (live)`
                  : `${pollingConfig.intervalMs/1000}s (standard)`
                }
              </span>
            </div>
          )}

          {/* Quick interval adjusters */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPollingConfig(prev => ({
                ...prev,
                intervalMs: Math.max(5000, prev.intervalMs - 5000),
                liveIntervalMs: Math.max(1000, prev.liveIntervalMs - 1000)
              }))}
              className="px-1 py-0.5 text-xs border rounded hover:bg-gray-100"
              style={{ borderColor: 'var(--border-default)', color: 'var(--fg-secondary)' }}
              title="Decrease interval (faster polling)"
            >
              −
            </button>
            <button
              onClick={() => setPollingConfig(prev => ({
                ...prev,
                intervalMs: Math.min(60000, prev.intervalMs + 5000),
                liveIntervalMs: Math.min(10000, prev.liveIntervalMs + 1000)
              }))}
              className="px-1 py-0.5 text-xs border rounded hover:bg-gray-100"
              style={{ borderColor: 'var(--border-default)', color: 'var(--fg-secondary)' }}
              title="Increase interval (slower polling)"
            >
              +
            </button>
            <button
              onClick={() => setPollingConfig(prev => ({ ...prev, enabled: !prev.enabled }))}
              className="px-2 py-0.5 text-xs border rounded hover:bg-gray-100"
              style={{ borderColor: 'var(--border-default)', color: 'var(--fg-secondary)' }}
              title="Toggle polling on/off"
            >
              {pollingConfig.enabled ? 'Pause' : 'Resume'}
            </button>
          </div>
        </div>
      </div>

      {/* Execution Selector */}
      <ExecutionSelector
        executionId={executionId}
        onExecutionChange={onExecutionChange || (() => {})}
        onLog={enhancedLogger}
        data-testid={`${testId}-execution-selector`}
      />

      {/* Event Filters */}
      <EventFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        data-testid={`${testId}-filters`}
      />

      {/* Stats & Controls */}
      <TestBlock
        title="Timeline Statistics"
        description="Current filter results and data controls"
        status={demoError ? 'error' : (events?.length || 0) === 0 ? 'warning' : 'success'}
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefetch}
              disabled={isLoading}
              className="flex items-center gap-1 px-2 py-1 rounded text-xs border transition-colors focus:outline-none focus:ring-2"
              style={{
                backgroundColor: 'var(--bg-surface)',
                borderColor: 'var(--border-default)',
                color: 'var(--fg-primary)',
                ['--hover-bg' as any]: 'var(--bg-interactive-hover)',
                ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--bg-surface)';
              }}
              onFocus={(e) => {
                e.currentTarget.style.boxShadow = 'var(--focus-ring)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
              data-testid={`${testId}-refresh`}
            >
              <RefreshCw className={cn("w-3 h-3", isLoading && "animate-spin")} />
              Refresh
            </button>
          </div>
        }
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div
            className="text-center p-3 rounded border"
            style={{
              backgroundColor: 'var(--bg-elevated)',
              borderColor: 'var(--border-subtle)'
            }}
          >
            <div
              className="text-lg font-mono"
              style={{ color: 'var(--accent)' }}
            >
              {totalEvents}
            </div>
            <div
              className="text-xs"
              style={{ color: 'var(--fg-muted)' }}
            >
              Total Events
            </div>
          </div>

          <div
            className="text-center p-3 rounded border"
            style={{
              backgroundColor: 'var(--bg-elevated)',
              borderColor: 'var(--border-subtle)'
            }}
          >
            <div
              className="text-lg font-mono"
              style={{ color: 'var(--accent)' }}
            >
              {events?.length || 0}
            </div>
            <div
              className="text-xs"
              style={{ color: 'var(--fg-muted)' }}
            >
              Loaded
            </div>
          </div>

          <div
            className="text-center p-3 rounded border"
            style={{
              backgroundColor: 'var(--bg-elevated)',
              borderColor: 'var(--border-subtle)'
            }}
          >
            <div
              className="text-lg font-mono"
              style={{ color: 'var(--accent)' }}
            >
              {Object.keys(filters).length}
            </div>
            <div
              className="text-xs"
              style={{ color: 'var(--fg-muted)' }}
            >
              Active Filters
            </div>
          </div>

          <div
            className="text-center p-3 rounded border"
            style={{
              backgroundColor: 'var(--bg-elevated)',
              borderColor: 'var(--border-subtle)'
            }}
          >
            <div
              className="text-lg font-mono"
              style={{ color: hasNextPage ? 'var(--accent)' : 'var(--fg-muted)' }}
            >
              {hasNextPage ? 'YES' : 'NO'}
            </div>
            <div
              className="text-xs"
              style={{ color: 'var(--fg-muted)' }}
            >
              More Pages
            </div>
          </div>
        </div>
      </TestBlock>

      {/* Invalid Execution ID Notice */}
      {!isValidExecution && (
        <TestBlock
          title="Invalid Execution ID"
          description="The selected execution ID is not a valid UUID format"
          status="error"
        >
          <div className="space-y-3">
            <div
              className="p-3 rounded border"
              style={{
                backgroundColor: 'var(--bg-error)',
                borderColor: 'var(--status-error)',
                color: 'var(--status-error)'
              }}
            >
              <p className="text-sm">
                Execution ID <code className="font-mono">{executionId}</code> is not a valid UUID.
              </p>
              <p className="text-xs mt-1">
                Expected format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
              </p>
            </div>
            <div
              className="text-xs p-2 rounded font-mono"
              style={{
                backgroundColor: 'var(--bg-elevated)',
                color: 'var(--fg-muted)'
              }}
            >
              Please select a valid execution from the dropdown above or trigger a new test pipeline.
            </div>
          </div>
        </TestBlock>
      )}

      {/* Error State - Unified Error Handling */}
      {demoError && isValidExecution && (
        <>
          {demoError.type === 'permission_denied' && (
            <PermissionNotice
              error={demoError}
              onRetry={handleRefetch}
              data-testid="temporal-permission-error"
            />
          )}
          {demoError.type === 'not_found' && (
            <NotFoundNotice
              error={demoError}
              executionId={executionId}
              onRetry={handleRefetch}
              data-testid="temporal-not-found-error"
            />
          )}
          {demoError.type === 'server_error' && (
            <ServerErrorNotice
              error={demoError}
              onRetry={handleRefetch}
              data-testid="temporal-server-error"
            />
          )}
          {demoError.type === 'network_error' && (
            <ServerErrorNotice
              error={demoError}
              onRetry={handleRefetch}
              data-testid="temporal-network-error"
            />
          )}
        </>
      )}

      {/* Vertical Timeline - Only show for valid execution IDs */}
      {isValidExecution && (
        <VerticalTimeline
          events={events}
          eventsByStage={eventsByStage}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onLoadMore={handleLoadMore}
          data-testid={`${testId}-timeline`}
          // DV-TEMP-004: Virtualization options
          enableVirtualization={true}
          virtualListHeight={500}
          itemHeight={120}
        />
      )}

      {/* DV-TEMP-004: Temporal Web Links for debugging */}
      <TestBlock
        title="Temporal Workflow Debugging"
        description="Direct links to Temporal Web UI for workflow execution details"
        status="info"
        defaultOpen={false}
      >
        <TemporalLinks
          executionId={executionId}
          workflowRunId={events[0]?.workflow_run_id}
          data-testid={`${testId}-temporal-links`}
        />
      </TestBlock>

      {/* Demo Info */}
      <TestBlock
        title="Demo Information"
        description="Technical implementation details"
        status="info"
        defaultOpen={false}
      >
        <div
          className="space-y-3 text-sm"
          style={{ color: 'var(--fg-primary)' }}
        >
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4" style={{ color: 'var(--accent)' }} />
            <span>Uses existing <code style={{ color: 'var(--accent)' }}>useExecutionEvents</code> hook</span>
          </div>
          <div>
            <strong>API Endpoint:</strong> <code style={{ color: 'var(--accent)' }}>/api/executions/{executionId}/events</code>
          </div>
          <div>
            <strong>Pagination:</strong> Cursor-based with server-side filtering
          </div>
          <div>
            <strong>URL Persistence:</strong> All filters sync to query parameters
          </div>
          <div>
            <strong>Real-time Updates:</strong> WebSocket integration via query invalidation
          </div>
        </div>
      </TestBlock>
    </div>
  );
}